import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';

import Layout from '/components/Layout';
import HeadMeta from '/components/HeadMeta';
import JobConfigurator from '/components/layout/JobConfigurator';
import { JobTemplate, JobDestinations } from '/types/jobTemplateTypes';
import Menu from 'front/types/frontMenuType';
import jobTypeApi from '/api/jobType';
import NotFound from '/pages/404';
import getMenus from '/lib/utils/getMenus';
import CoverageInfo from '/components/layout/JobConfigurator/CoverageInfo';

interface Props {
    job: JobTemplate;
    menus: Menu[];
}

const Job = ({ job, menus }: Props) => {
    if (!job) return <NotFound />;

    return (
        <Layout menus={menus} jobType={job.typeKey}>
            <HeadMeta
                {...(job?.jobTypeVersion?.info?.seo || {})}
                links={[
                    {
                        rel: 'canonical',
                        href:
                            process.env.NEXT_PUBLIC_APP_URL +
                            '/bestilling/jobb/' +
                            job.jobTypeVersion.slug,
                    },
                ]}
            />
            <CoverageInfo jobType={job.typeKey} />
            <div className="pt-80">
                <JobConfigurator
                    job={job}
                    jobDestination={JobDestinations.CART}
                />
            </div>
        </Layout>
    );
};

export default Job;

export async function getStaticPaths() {
    const resp = await jobTypeApi.index();

    if ('error' in resp) {
        throw new Error(
            `API was unable to serve job types required for SSG: ${resp.error}`
        );
    }

    return {
        paths: resp.data
            .filter((job) => job.published)
            .map((job) => ({ params: { slug: job.jobTypeVersion.slug } })),
        fallback: 'blocking',
    };
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
    const resp = await jobTypeApi.get(String(params?.slug));

    if ('error' in resp) {
        return { notFound: true };
    }

    const menus: Menu[] = await getMenus();

    return {
        props: {
            job: resp.data,
            menus,
        },
        revalidate: 60,
    };
};
